import { z } from 'zod';
const InternalId = z.string().regex(/^[1-9][0-9]{0,23}$/);
const ServiceTitanUrl = z
    .string()
    .url()
    .max(2000)
    .refine((value) => {
    try {
        const url = new URL(value);
        return (url.protocol === 'https:' &&
            (url.hostname === 'servicetitan.com' ||
                url.hostname.endsWith('.servicetitan.com')) &&
            !url.username &&
            !url.password &&
            !url.search &&
            !url.hash.includes('?'));
    }
    catch {
        return false;
    }
}, 'Use a stable HTTPS ServiceTitan record URL without credentials or query parameters.');
function namesRecord(value, kind, id) {
    try {
        const url = new URL(value);
        const route = url.hash ? url.hash.slice(1) : url.pathname;
        const recordRoute = kind === 'job' ? '(?:(?:new/)?jobs?|job/index)' : '(?:new/)?projects?';
        return new RegExp(`^/${recordRoute}/${id}(?:/|$)`, 'i').test(route);
    }
    catch {
        return false;
    }
}
/** The authenticated collector attests to rendered source relationships, not inferred IDs. */
export const ProjectJobLinkSchema = z
    .object({
    st_project_id: InternalId,
    st_job_id: InternalId,
    relationship: z.enum([
        'sales',
        'service',
        'site_check',
        'installation',
        'other',
    ]),
    source_url: ServiceTitanUrl,
    observed_at: z.string().datetime({ offset: true }),
    verification: z
        .object({
        method: z.literal('project_jobs_and_job_project_link'),
        tenant: z.string().min(1).max(100),
        job_source_url: ServiceTitanUrl,
    })
        .strict()
        .nullable()
        .default(null),
})
    .strict()
    .superRefine((link, ctx) => {
    if (!namesRecord(link.source_url, 'project', link.st_project_id))
        ctx.addIssue({
            code: 'custom',
            path: ['source_url'],
            message: 'Source URL must identify the observed internal project ID.',
        });
    if (link.verification &&
        !namesRecord(link.verification.job_source_url, 'job', link.st_job_id))
        ctx.addIssue({
            code: 'custom',
            path: ['verification', 'job_source_url'],
            message: 'Job URL must identify the observed internal job ID.',
        });
    if (Date.parse(link.observed_at) > Date.now() + 300000)
        ctx.addIssue({
            code: 'custom',
            path: ['observed_at'],
            message: 'Observation timestamp is in the future.',
        });
});
export const ProjectJobLinksSchema = z
    .array(ProjectJobLinkSchema)
    .max(100)
    .superRefine((links, ctx) => {
    if (new Set(links.map((link) => link.st_job_id)).size !== links.length)
        ctx.addIssue({
            code: 'custom',
            message: 'Record each job relationship once.',
        });
});
export const ResolvePreSiteSchema = z
    .object({ project_job_links: ProjectJobLinksSchema })
    .strict();
export const ProjectJobLinkConflictSchema = z.object({
    st_job_id: z.string(),
    code: z.enum(['LINKED_TO_DIFFERENT_PROJECT', 'SOURCE_PROJECT_CONFLICT']),
    conflicting_project_id: z.string(),
    source_review_id: z.string().nullable(),
});
export const ProjectJobLinkStatusSchema = z.object({
    association_fingerprint: z.string(),
    status: z.enum(['verified', 'unverified']),
    observation: ProjectJobLinkSchema,
});
export const PreSiteSourcesSchema = z.object({
    associations: z.array(ProjectJobLinkStatusSchema),
    conflicts: z.array(ProjectJobLinkConflictSchema),
});
/** Unavailable is distinct from a successful lookup with no linked source. */
export const LivePreSiteSourcesSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('available'),
        checked_at: z.string().datetime(),
        revision: z.string(),
        sources: PreSiteSourcesSchema,
    }),
    z.object({
        status: z.literal('unavailable'),
        checked_at: z.string().datetime(),
    }),
]);
